import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Tooltip, TooltipTrigger, TooltipContent } from "./tooltip"

import { cn } from "../../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-light/20 disabled:pointer-events-none disabled:opacity-30 dark:ring-offset-brand-dark [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "box-border border border-brand-gold bg-brand-gold text-white hover:bg-brand-gold/90 dark:border-brand-gold dark:bg-brand-gold dark:hover:bg-brand-gold/90",
        destructive: "box-border border border-brand-red bg-brand-red text-brand-white hover:bg-brand-red/90 dark:border-brand-red dark:bg-brand-red dark:hover:bg-brand-red/90",
        outline: "box-border border border-brand-border bg-brand-background text-foreground hover:bg-brand-light hover:border-brand-border hover:text-brand-white dark:border-brand-border dark:bg-brand-background dark:text-brand-white dark:hover:border-brand-border dark:hover:text-brand-white dark:hover:bg-brand-light",
        secondary: "box-border border border-brand-border bg-secondary text-brand-white hover:bg-secondary/80 dark:border-brand-border dark:bg-secondary dark:text-brand-white dark:hover:bg-secondary/80",
        ghost: "box-border border border-transparent hover:bg-brand-light hover:text-brand-white dark:hover:bg-brand-light dark:hover:text-brand-white",
        link: "text-brand-light underline-offset-4 hover:underline dark:text-brand-white",
        tertiary: "box-border border border-brand-green/30 bg-background text-foreground hover:bg-brand-light hover:border-brand-green hover:text-brand-green dark:border-brand-green/30 dark:bg-brand-green/10 dark:text-brand-green dark:hover:border-brand-green dark:hover:text-brand-green dark:hover:bg-brand-green/20",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3",
        lg: "h-11 px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  description?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, description, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    const button = (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )

    if (description) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            {button}
          </TooltipTrigger>
          <TooltipContent>
            {description}
          </TooltipContent>
        </Tooltip>
      )
    }

    return button
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
